import Select from 'react-select';

import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown, Offcanvas, ListGroup } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2';
import axios from 'axios';

// TypeScript interfaces for MasterJabatan API response
export interface MasterJabatanResponse {
  message: string;
  data: MasterJabatan[];
}

export interface MasterJabatan {
  id: number;
  jabatan: string;
  type: string;
  created_at: string;
  updated_at: string;
  status: number;
  use_channels: number;
  deleted: number;
  settings: JabatanSettings;
}

export interface JabatanSettings {
  [key: string]: Record<string, any>;
}

export interface FormDataState {
  jabatan: string;
  type: string;
  status: number;
  use_channels: number;
  deleted: number;
  custom_settings: string;
  selected_setting: number | '';
}

const Jabatan = () => {
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false); // State untuk modal upload Excel
  const [formData, setFormData] = useState<FormDataState>({
    jabatan: '',
    type: '',
    status: 0, // Default status set to 0
    use_channels: 0,
    deleted: 0,
    custom_settings: '', // Tidak ada default selected
    selected_setting: '', // Untuk select setting
  }); // Updated form data state
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [tableData, setTableData] = useState<MasterJabatan[]>([]); // Use MasterJabatan type for tableData

  const [showOffcanvas, setShowOffcanvas] = useState(false); // State for Offcanvas visibility
  const [selectedJabatan, setSelectedJabatan] = useState<string | null>(null); // State for selected Jabatan

  const [showChannelModal, setShowChannelModal] = useState(false); // State for Add Channel modal
  const [channelFormData, setChannelFormData] = useState({ name: '', link: '' }); // Add link field to form data

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  const handleShowImportModal = () => setShowImportModal(true);
  const handleCloseImportModal = () => setShowImportModal(false);

  const handleShowOffcanvas = (jabatan: string) => {
    setSelectedJabatan(jabatan);
    setShowOffcanvas(true);
  };

  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
    setSelectedJabatan(null);
  };

  const handleShowChannelModal = () => setShowChannelModal(true);
  const handleCloseChannelModal = () => {
    setShowChannelModal(false);
    setChannelFormData({ name: '', link: '' }); // Reset form data
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: id === 'status' || id === 'use_channels' || id === 'deleted' ? parseInt(value) : value });
  };

  const handleChannelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setChannelFormData({ ...channelFormData, [id]: value });
  };

  const handleFormSubmit = async () => {
    if (!formData.jabatan.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Jabatan is required',
        text: 'Please enter the name of the position.',
      });
      return;
    }
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.post(
        '/master-jabatan',
        {
          jabatan: formData.jabatan,
          type: formData.type,
          status: formData.status,
          use_channels: formData.use_channels,
          deleted: 0, // Ensure deleted is always 0
          custom_settings: formData.custom_settings,
          selected_setting: formData.selected_setting,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to Authorization header
          },
        }
      );

      Swal.fire('Success', `Jabatan "${response.data.jabatan}" added successfully!`, 'success');
      setShowModal(false);
      setFormData({ jabatan: '', type: '', status: 0, use_channels: 0, deleted: 0, custom_settings: 'no', selected_setting: '' }); // Reset form
      fetchJabatan(); // Refetch jabatan to update the table
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleChannelFormSubmit = () => {
    console.log('Channel added:', channelFormData); // Replace with actual logic
    handleCloseChannelModal();
  };

  const fetchJabatan = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/master-jabatan', {
        headers: {
          Authorization: `Bearer ${token}`, // Add token to Authorization header
        },
      });

      setTableData(response.data.data || []); // Update table data with API response
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000, // Smooth transition after loading
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch jabatan',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true); // Start loading
        try {
          const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.patch(`/master-jabatan/${id}/delete`, null, {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          });

          Swal.fire('Deleted!', 'The jabatan has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id)); // Remove deleted item from table
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to delete jabatan',
            'error'
          );
        } finally {
          setIsLoading(false); // End loading
        }
      }
    });
  };

  const handleToggle = async (id: number, field: 'status' | 'use_channels', value: number) => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Updating...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      await axios.patch(
        `/master-jabatan/${id}`,
        { [field]: value },
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to Authorization header
          },
        }
      );

      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, [field]: value } : item
        )
      );
      Swal.fire('Success', `The ${field} has been updated.`, 'success');
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || `Failed to update ${field}`,
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleDownloadTemplate = async (id: number) => {
    setIsLoading(true);
    Swal.fire({
      title: 'Preparing download...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get(`/master-jabatan/${id}/template`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = (response.headers && response.headers['content-disposition']) || '';
      const match = /filename="?([^"]+)"?/.exec(contentDisposition);
      link.setAttribute('download', (match && match[1]) || `template_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire('Success', 'Template downloaded.', 'success');
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to download template',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Stub implementation for importing settings to avoid "Cannot find name 'handleImport'" compile error.
  // Replace with actual import logic as needed.
  // Modal upload Excel
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importId, setImportId] = useState<number | null>(null);
  const handleImport = (id: number) => {
    setImportId(id);
    setShowImportModal(true);
  };
  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFile(e.target.files[0]);
    }
  };
  const handleImportSubmit = async () => {
    if (!importFile || !importId) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      const formData = new FormData();
      formData.append('file', importFile);
      await axios.post(`/master-jabatan/${importId}/import-meta`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      Swal.fire('Success', 'File imported successfully!', 'success');
      setShowImportModal(false);
      setImportFile(null);
      setImportId(null);
      fetchJabatan();
    } catch (error) {
      Swal.fire('Error', (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to import file', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJabatan(); // Fetch jabatan on component mount
  }, []); // Fetch jabatan on component mount

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Jabatan" title="Jabatan" />

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0"></h5>
                    <div className="ms-auto"> {/* Add ms-auto to push the button to the right */}
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Jabatan</th>
                        <th>Status</th>
                        <th>Settings</th>
                        <th>Use Channels</th>
                        <th>Channels</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? ( // Ensure tableData is an array and not empty
                        tableData.map((jabatan, index) => (
                          <tr key={jabatan.id}>
                            <td>{index + 1}</td>
                            <td>{jabatan.jabatan}</td>
                            <td>
                              <div className="form-check form-switch">
                                <Form.Check
                                  type="checkbox"
                                  role="switch"
                                  id={`status-${jabatan.id}`}
                                  checked={jabatan.status === 1}
                                  onChange={(e) => handleToggle(jabatan.id, 'status', e.target.checked ? 1 : 0)}
                                  disabled={isLoading} // Disable toggle during loading
                                />
                              </div>
                            </td>
                            <td>
                              {jabatan.settings && typeof jabatan.settings === 'object' ? (
                                Object.keys(jabatan.settings).length > 0 ? (
                                  <div style={{ display: 'block' }}>
                                    {Object.entries(jabatan.settings).map(([key, value]) => (
                                      typeof value === 'object' && value !== null ? (
                                        <div key={key} style={{ marginBottom: '2px' }}>
                                          <span className={`badge bg-${value.setting_status === 'complete' ? 'success' : value.setting_status === 'no' ? 'danger' : 'warning'}`}>
                                            {key.toUpperCase()}: {value.setting_status ? String(value.setting_status).toUpperCase() : '-'}
                                          </span>
                                        </div>
                                      ) : (
                                        <div key={key} style={{ marginBottom: '2px' }}>
                                          <span className="badge bg-warning">
                                            {key.toUpperCase()}: {JSON.stringify(value).toUpperCase()}
                                          </span>
                                        </div>
                                      )
                                    ))}
                                  </div>
                                ) : (
                                  <span className="badge bg-secondary">No Settings</span>
                                )
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <Form.Check
                                  type="checkbox"
                                  role="switch"
                                  id={`use_channels-${jabatan.id}`}
                                  checked={jabatan.use_channels === 1}
                                  onChange={(e) => handleToggle(jabatan.id, 'use_channels', e.target.checked ? 1 : 0)}
                                  disabled={isLoading} // Disable toggle during loading
                                />
                              </div>
                            </td>
                            <td>
                              {jabatan.use_channels === 1 ? (
                                <Button
                                  variant="dark"
                                  size="sm"
                                  onClick={() => handleShowOffcanvas(jabatan.jabatan)}
                                  disabled={isLoading} // Disable button during loading
                                >
                                  List Channels
                                </Button>
                              ) : (
                                'Disabled'
                              )}
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="dark" id={`action-dropdown-${jabatan.id}`} className="btn-sm">
                                  <i className="ri-more-2-fill"></i> {/* Add an icon for the dropdown toggle */}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => console.log('Edit', jabatan.id)}>
                                    <i className="ri-edit-line me-2"></i> Edit {/* Add an edit icon */}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDelete(jabatan.id)}>
                                    <i className="ri-delete-bin-line me-2"></i> Delete {/* Add a delete icon */}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDownloadTemplate(jabatan.id)}>
                                    <i className="ri-download-2-line me-2"></i> Download Template {/* Add a download icon */}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleImport(jabatan.id)}>
                                    <i className="ri-upload-2-line me-2"></i> Upload Settings {/* Add an upload icon */}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center">
                            No jabatan available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Adding Jabatan */}
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Jabatan</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="jabatan">Jabatan</Form.Label>
                <Form.Control
                  type="text"
                  id="jabatan"
                  placeholder="Enter the name of the position"
                  value={formData.jabatan}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.jabatan.trim()} // Validation feedback
                />
                <Form.Control.Feedback type="invalid">
                  Jabatan is required.
                </Form.Control.Feedback>
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="use_channels">Use Channels</Form.Label>
                    <div className="form-check form-switch">
                      <Form.Check
                        type="checkbox"
                        role="switch"
                        id="use_channels"
                        checked={formData.use_channels === 1}
                        onChange={(e) =>
                          setFormData({ ...formData, use_channels: e.target.checked ? 1 : 0 })
                        }
                        disabled={isLoading}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Custom Settings</Form.Label>
                    <div>
                      <Form.Check
                        type="radio"
                        label="Yes"
                        name="custom_settings"
                        id="custom_settings_yes"
                        checked={formData.custom_settings === 'yes'}
                        onChange={() => setFormData({ ...formData, custom_settings: 'yes', selected_setting: '' })}
                        disabled={isLoading}
                      />
                      <Form.Check
                        type="radio"
                        label="No"
                        name="custom_settings"
                        id="custom_settings_no"
                        checked={formData.custom_settings === 'no'}
                        onChange={() => setFormData({ ...formData, custom_settings: 'no' })}
                        disabled={isLoading}
                      />
                    </div>
                  </Form.Group>
                  {formData.custom_settings === 'no' && (
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="selected_setting">Select Setting</Form.Label>
                      <Select
                        id="selected_setting"
                        options={tableData.map(jabatan => ({ value: jabatan.id, label: jabatan.jabatan }))}
                        value={tableData
                          .map(jabatan => ({ value: jabatan.id, label: jabatan.jabatan }))
                          .find(option => option.value === formData.selected_setting) || null}
                        onChange={(option: { value: number; label: string } | null) => setFormData({ ...formData, selected_setting: option ? option.value : '' })}
                        isDisabled={isLoading}
                        placeholder="-- Select Jabatan --"
                        isClearable
                      />
                    </Form.Group>
                  )}
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="dark" onClick={handleCloseModal} disabled={isLoading}>
              Close
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Offcanvas for List Channels */}
        <Offcanvas show={showOffcanvas} onHide={handleCloseOffcanvas} placement="end" style={{ width: '600px' }}> {/* Increased width */}
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Channels for {selectedJabatan}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <Button
              variant="dark"
              size="sm"
              className="mb-3"
              onClick={handleShowChannelModal} // Show Add Channel modal
            >
              <i className="ri-add-line align-middle me-2"></i> Add Channel
            </Button>
            <ListGroup as="ul">
              {Array.from({ length: 20 }, (_, index) => (
                index < 15 && (
                  <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                    <div>
                      <i className="ri-whatsapp-line align-middle me-2" style={{ color: '#25D366' }}></i>
                      Dummy Channel {index + 1}
                    </div>
                    <div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-primary me-2"
                        onClick={() => console.log('Edit Channel', index + 1)} // Replace with actual edit logic
                      >
                        <i className="ri-edit-line"></i>
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => console.log('Delete Channel', index + 1)} // Replace with actual delete logic
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </div>
                  </ListGroup.Item>
                )
              ))}
            </ListGroup>
          </Offcanvas.Body>
        </Offcanvas>

        {/* Modal for Adding Channel */}
        <Modal show={showChannelModal} onHide={handleCloseChannelModal} backdrop="static" centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Channel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="name">Channel Name</Form.Label>
                <Form.Control
                  type="text"
                  id="name"
                  placeholder="Enter channel name"
                  value={channelFormData.name}
                  onChange={handleChannelInputChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="link">Channel Link</Form.Label>
                <Form.Control
                  type="text"
                  id="link"
                  placeholder="Enter channel link"
                  value={channelFormData.link}
                  onChange={handleChannelInputChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseChannelModal}>
              Close
            </Button>
            <Button variant="dark" onClick={handleChannelFormSubmit}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal Upload Excel */}
        <Modal show={showImportModal} onHide={handleCloseImportModal} backdrop="static" centered>
          <Modal.Header closeButton>
            <Modal.Title>Upload Excel Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="importFile">Select Excel File (.xlsx)</Form.Label>
                <Form.Control
                  type="file"
                  id="importFile"
                  accept=".xlsx"
                  onChange={handleImportFileChange}
                  disabled={isLoading}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseImportModal} disabled={isLoading}>
              Close
            </Button>
            <Button variant="dark" onClick={handleImportSubmit} disabled={isLoading || !importFile}>
              {isLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Jabatan;